import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, apiError } from "@/lib/api";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-slate-50">
      <div className="relative hidden flex-col justify-between overflow-hidden bg-slate-950 p-10 text-slate-100 lg:flex lg:w-[46%]">
        <div className="absolute -top-24 -left-20 h-80 w-80 rounded-full bg-sky-500/30 blur-3xl" />
        <div className="absolute bottom-0 right-0 h-96 w-96 translate-x-1/3 translate-y-1/3 rounded-full bg-blue-700/30 blur-3xl" />
        <div className="relative flex items-center gap-3">
          <img src="/dolphin-logo.png" alt="Dolphin Digital OOH Media" className="h-16 w-auto object-contain" />
        </div>
        <div className="relative space-y-5">
          <h1 className="text-4xl font-bold leading-tight tracking-tight">Forgot your<br /><span className="bg-gradient-to-r from-sky-300 to-blue-200 bg-clip-text text-transparent">password?</span></h1>
          <p className="max-w-md text-sm leading-relaxed text-slate-300">No worries—enter your work email and we’ll send you a secure link to choose a new one. The link expires in 60 minutes.</p>
        </div>
        <p className="relative text-xs text-slate-500">Dolphin Digital OOH Media © {new Date().getFullYear()}</p>
      </div>

      <div className="relative flex flex-1 items-center justify-center bg-grid p-6 lg:p-12">
        <div className="w-full max-w-md fade-up">
          <div className="mb-8 flex items-center gap-3 lg:hidden rounded-xl bg-slate-950 px-4 py-3">
            <img src="/dolphin-logo.png" alt="Dolphin Digital OOH Media" className="h-12 w-auto object-contain" />
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-900/5">
            {sent ? (
              <div className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="h-6 w-6" />
                </div>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900">Check your inbox</h2>
                <p className="mt-2 text-sm text-slate-600">If an account exists for <span className="font-medium text-slate-900">{email}</span>, we just sent a password reset link. It expires in 60 minutes.</p>
                <p className="mt-4 text-xs text-slate-500">Didn’t receive it? Check your spam folder, or try again in a few minutes.</p>
                <div className="mt-6 flex flex-col gap-2">
                  <Button onClick={() => { setSent(false); setEmail(""); }} variant="outline" className="w-full">Use a different email</Button>
                  <Link to="/login" className="text-sm font-medium text-sky-700 hover:text-sky-900">← Back to sign in</Link>
                </div>
              </div>
            ) : (
              <>
                <Link to="/login" className="mb-4 inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </Link>
                <h2 className="text-2xl font-bold tracking-tight text-slate-900">Forgot password?</h2>
                <p className="mt-1 text-sm text-slate-500">Enter your work email and we’ll send you a reset link.</p>
                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  {error && <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</p>}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                      <Input id="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="pl-9" required />
                    </div>
                  </div>
                  <Button type="submit" className="w-full h-11 bg-slate-900 hover:bg-slate-800 transition-colors" disabled={loading}>
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {loading ? "Sending…" : "Send reset link"}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
